<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import CaptionService from '@/services/CaptionService'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import { track, EVENTS } from '@/lib/tracking'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import Spinner from '@/components/ui/Spinner.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconClosedCaption from '@/components/icon/IconClosedCaption.vue'
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue'
import ViewModeSwitcher from '@/components/ui/ViewModeSwitcher.vue'
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue'
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue'
import CaptioningFlowCard from '@/components/Captioning/CaptioningFlowCard.vue'
import AddCaptioningModal from '@/components/modals/AddCaptioningModal.vue'
import CaptionProviderBanner from '@/components/Captioning/CaptionProviderBanner.vue'
import { ENGINE_SYNCWORDS, isSyncWordsFlow, providerUsageLabel } from '@/components/Captioning/captionSyncWords'
import {
	DEFAULT_LIMITS,
	DEFAULT_USAGE,
	UPGRADE_ROUTE,
	hasPool,
	notIncluded,
	poolExhausted,
	poolNearlyUsed,
	poolExhaustedTooltip,
	usageBarMeta,
	formatHours,
	formatResetDate,
	describeHours,
} from '@/components/Captioning/captionLimits'

const router = useRouter()
const store = useStore()
const { notify } = useNotification()

const loading = ref(true)
const loadError = ref('')
const flows = ref([])
// Plan limits and usage from the list response (defaults for an older core).
const limits = ref({ ...DEFAULT_LIMITS })
const usage = ref({ ...DEFAULT_USAGE })
// flowId -> status payload. Core attaches a lightweight `summary` to each flow
// in the list, which drives the status dots. Only an older core without
// summaries (`summary === undefined`) triggers one status fetch per flow.
const statuses = ref({})
const search = ref('')
const createOpen = ref(false)
const deleteTarget = ref(null)
// GET /captions/engine: which engine new flows get and whose SyncWords account they use.
const engineInfo = ref(null)
// The own SyncWords connection with its usage this month: the list's `provider`, else GET /captions/provider.
const listProvider = ref(null)
let providerRequested = false
let alive = true

const compact = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])

const filteredFlows = computed(() => {
	const q = search.value.trim().toLowerCase()
	if (!q) return flows.value
	return flows.value.filter((f) =>
		(f.streamName || '').toLowerCase().includes(q)
		|| (f.streamKey || '').toLowerCase().includes(q),
	)
})

const takenStreamKeys = computed(() => flows.value.map((f) => f.streamKey))
// Only where SyncWords captioning is in play: new flows go to it, or some flow already does.
const showProviderBanner = computed(() => engineInfo.value?.engine === ENGINE_SYNCWORDS || flows.value.some(isSyncWordsFlow))
const provider = computed(() => listProvider.value || engineInfo.value?.provider || null)
// With an own account connected, new captioning runs on it: the header shows its usage, not Castr's plan.
const ownAccount = computed(() => !!provider.value?.connected)
const providerUsageText = computed(() => (ownAccount.value ? providerUsageLabel(listProvider.value?.usage) : ''))
const providerUsageShort = computed(() => (providerUsageText.value ? providerUsageText.value.replace(' captioned this month on your SyncWords account', ' this month') : ''))

// Plan gates. `enforced: false` is reporting mode: the bar and notices show,
// nothing is blocked. An older core (no `limits`) lands on the defaults: no
// pool, not enforced, which is the pre-limits UI.
const upgradeRequired = computed(() => notIncluded(limits.value))
const exhausted = computed(() => !ownAccount.value && poolExhausted(limits.value, usage.value))
const nearlyUsed = computed(() => !ownAccount.value && !exhausted.value && poolNearlyUsed(limits.value, usage.value))
// No bar behind the paywall: the upgrade view speaks for itself.
const headerUsage = computed(() => (upgradeRequired.value || ownAccount.value ? null : usageBarMeta(limits.value, usage.value)))
// The beta badge only survives where there is no pool and nothing is enforced.
const showBetaBadge = computed(() => !ownAccount.value && !hasPool(limits.value) && !limits.value.enforced)
const exhaustedTooltip = computed(() => poolExhaustedTooltip(limits.value))
const resetDate = computed(() => formatResetDate(limits.value.periodEnd))
const poolNoticeText = computed(() => {
	if (!hasPool(limits.value)) return ''
	if (exhausted.value) {
		return `You've used all ${describeHours(limits.value.poolSec)} this cycle${resetDate.value ? ` · resets ${resetDate.value}` : ''}.`
	}
	return `${formatHours(usage.value.usedSec)} of ${formatHours(limits.value.poolSec)} used — upgrade for more hours.`
})
const emptyStateNote = computed(() => (showBetaBadge.value ? 'Free during beta. One flow per live stream.' : 'One flow per live stream.'))

// One event per reason per visit: a disabled control being hovered or clicked.
const capHitsNoted = new Set()
function noteCapHit(action, code = 'captions_pool_exhausted') {
	if (!exhausted.value && !upgradeRequired.value) return
	const key = `${code}:${action}`
	if (capHitsNoted.has(key)) return
	capHitsNoted.add(key)
	track(EVENTS.CAPTIONING_CAP_HIT, { code, action, surface: 'dashboard' })
}

function openCreate() {
	if (exhausted.value) {
		noteCapHit('click')
		return
	}
	createOpen.value = true
}

function applyList(res) {
	if (res.provider) listProvider.value = res.provider
	flows.value = res.flows.slice()
	limits.value = res.limits
	usage.value = res.usage
}

async function load() {
	const cached = CaptionService.cachedList()
	// A cached list paints immediately; the request refreshes it in the background.
	// A cold visit still waits on the one list request.
	if (cached) {
		applyList(cached)
		loading.value = false
	} else {
		loading.value = true
	}
	loadError.value = ''
	try {
		const res = await CaptionService.listFlows()
		if (!alive) return
		applyList(res)
		refreshStatuses()
	} catch (err) {
		if (!cached) {
			loadError.value = err?.message || 'Could not load your captioning flows.'
		}
	} finally {
		if (alive) loading.value = false
	}
}

/** An older core's list has no `provider`: ask for the connection once SyncWords is in play. */
watch([showProviderBanner, loading], ([inPlay, isLoading]) => {
	if (!inPlay || isLoading || listProvider.value || providerRequested) return
	providerRequested = true
	CaptionService.getProvider()
		.then((p) => { if (alive) listProvider.value = { connected: p.connected, account: p.account, usage: p.providerUsage } })
		.catch(() => {})
})

async function refreshStatuses() {
	const legacy = flows.value.filter((f) => f.configured && !f.streamDeleted && f.summary === undefined)
	await Promise.allSettled(legacy.map(async (flow) => {
		try {
			const status = await CaptionService.getStatus(flow.id)
			if (alive) statuses.value = { ...statuses.value, [flow.id]: status }
		} catch {
			// The card falls back to the flow's configured/paused flags.
		}
	}))
}

function onCreated(flow) {
	flows.value = [flow, ...flows.value]
	createOpen.value = false
	// A SyncWords flow lands with Start focused: captions start before the encoder can connect.
	router.push({ name: 'CaptioningManage', params: { flowId: flow.id }, ...(flow.engine === 'syncwords' ? { state: { captionsCreated: true } } : {}) })
}

function requestDelete(flow) {
	deleteTarget.value = flow
	showModal('modal-captioning-list-delete')
}

async function confirmDelete() {
	const target = deleteTarget.value
	if (!target) return
	try {
		await CaptionService.deleteFlow(target.id)
		flows.value = flows.value.filter((f) => f.id !== target.id)
		track(EVENTS.CAPTIONING_DELETED, {})
		notify({ group: 'success', text: `Captioning for ${target.streamName || 'this stream'} was deleted.` })
	} catch (err) {
		notify({ group: 'error', title: 'Could not delete captioning', text: err?.message })
	} finally {
		deleteTarget.value = null
	}
}

onMounted(() => {
	load()
	CaptionService.getEngine().then((info) => { if (alive) engineInfo.value = info }).catch(() => {})
	// Preload live streams for the create modal so it opens instantly.
	store.dispatch('Streams/getStreams').catch(() => {})
})

onBeforeUnmount(() => {
	alive = false
})
</script>

<template>
	<div class="container detail-shell px-4">
		<PageHeader
			title="Captioning"
			subtitle="Add live captions and translations to your streams."
			mobile-inline
			show-meta-on-mobile
			:usage="!loading ? headerUsage : null"
		>
			<template v-if="!loading && providerUsageText" #meta>
				<span class="hidden text-tiny text-surface-8 sm:inline" data-vendor-name data-testid="header-provider-usage">{{ providerUsageText }}</span>
				<!-- Narrow screens: the title needs the room; the full sentence is the tooltip. -->
				<span class="whitespace-nowrap text-tiny text-surface-8 sm:hidden" :title="providerUsageText" data-vendor-name data-testid="header-provider-usage-short">{{ providerUsageShort }}</span>
			</template>
			<template v-else-if="!loading && showBetaBadge" #meta>
				<span
					class="inline-flex h-6 items-center gap-1.5 rounded-full bg-main-transparent px-2.5 text-[11px] font-semibold uppercase tracking-wide text-main-light ring-1 ring-main/30"
					title="Captioning is free while in beta. Pricing will be announced before it changes."
				>
					Free during beta
				</span>
			</template>
			<span
				v-if="!loading && !upgradeRequired"
				class="inline-flex"
				data-testid="create-captioning"
				@mouseenter="noteCapHit('hover')"
				@click="exhausted && noteCapHit('click')"
			>
				<Tooltip :active="exhausted" :content="exhaustedTooltip" wrap-trigger :hide="800">
					<FormButton type="main" size="md" class="gap-1" :disabled="exhausted" @click="openCreate">
						<icon-base class="mr-1" fill="none">
							<icon-plus />
						</icon-base>
						New <span class="hidden md:inline">captioning</span>
					</FormButton>
				</Tooltip>
			</span>
		</PageHeader>

		<div v-if="loading" class="flex min-h-[60vh] w-full items-center justify-center" aria-busy="true">
			<Spinner text="Loading your captioning flows…" classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
		</div>

		<main v-else>
			<CaptionProviderBanner v-if="showProviderBanner && !upgradeRequired" class="mb-4" :provider="provider" />
			<Alert v-if="loadError" type="error" class="mb-4">
				<div class="flex w-full flex-wrap items-center gap-2">
					<span class="flex-1">{{ loadError }}</span>
					<FormButton type="secondary" size="sm" @click="load">Retry</FormButton>
				</div>
			</Alert>

			<!-- Plan without captioning: the upgrade view, like Live Clipping. -->
			<section v-else-if="upgradeRequired" data-testid="captioning-upgrade">
				<EmptyState
					title="Upgrade to unlock Captioning"
					subtitle="Add live captions and translations to your streams. Captioning isn't included in your current plan."
				>
					<template #icon>
						<icon-base width="28" height="28" fill="none">
							<icon-closed-caption />
						</icon-base>
					</template>
					<template #actions>
						<div class="mx-auto flex w-full max-w-72 flex-col" @click="track(EVENTS.UPGRADE_CLICKED, { source: 'captioning' })">
							<FormButton type="main" size="md" full-width class="justify-center" :to="UPGRADE_ROUTE">
								Upgrade now
							</FormButton>
						</div>
					</template>
				</EmptyState>
			</section>

			<template v-else>
				<Alert
					v-if="poolNoticeText && (exhausted || nearlyUsed)"
					:type="exhausted ? 'warning2' : 'normal'"
					class="mb-4"
					role="status"
					data-testid="pool-notice"
				>
					<span class="flex w-full flex-wrap items-center gap-x-1 gap-y-1">
						<span>{{ poolNoticeText }}</span>
						<router-link :to="UPGRADE_ROUTE" class="font-medium underline" :class="exhausted ? 'text-white' : 'text-main-light'" @click="track(EVENTS.UPGRADE_CLICKED, { source: 'captioning' })">
							{{ exhausted ? 'Upgrade for more hours' : 'Upgrade' }}
						</router-link>
					</span>
				</Alert>

				<section v-if="flows.length === 0">
					<EmptyState
						title="Caption your live streams"
						subtitle="Create a captioning flow, pick a stream, choose the languages, and captions appear in the player within about a minute."
					>
						<template #icon>
							<icon-base width="28" height="28" fill="none">
								<icon-closed-caption />
							</icon-base>
						</template>
						<template #actions>
							<div class="mx-auto flex w-full max-w-72 flex-col">
								<Tooltip :active="exhausted" :content="exhaustedTooltip" wrap-trigger wrapper-class="w-full" :hide="800">
									<FormButton type="main" size="md" full-width class="justify-center gap-1" :disabled="exhausted" @click="openCreate">
										<icon-base class="mr-1" fill="none">
											<icon-plus />
										</icon-base>
										New captioning
									</FormButton>
								</Tooltip>
								<p class="mb-0 mt-3 text-tiny text-surface-7">{{ emptyStateNote }}</p>
							</div>
						</template>
					</EmptyState>
				</section>

				<section v-else>
					<ResponsiveToolbar class="my-3">
						<template #leading>
							<span class="text-tiny text-surface-8">
								{{ flows.length }} {{ flows.length === 1 ? 'flow' : 'flows' }}
							</span>
						</template>
						<ListingSearchInput v-model="search" placeholder="Search by stream name or key" />
						<ViewModeSwitcher class="shrink-0" />
					</ResponsiveToolbar>

					<div
						class="mt-3 grid auto-rows-fr"
						:class="compact ? '!grid-cols-1 gap-1.5' : 'grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'"
					>
						<CaptioningFlowCard
							v-for="flow in filteredFlows"
							:key="flow.id"
							:flow="flow"
							:status="statuses[flow.id] || null"
							:compact="compact"
							@delete="requestDelete"
						/>
						<!-- The tile's limit copy is count-based; with the pool used up the header explains instead. -->
						<ListingCreateTile
							v-if="!search && !exhausted"
							label="captioning"
							:view="compact ? 'list' : 'thumbnail'"
							:used="flows.length"
							@create="openCreate"
						/>
					</div>
					<p v-if="search && filteredFlows.length === 0" class="mt-6 text-center text-tiny text-surface-7">
						No captioning flow matches "{{ search }}".
					</p>
				</section>
			</template>
		</main>

		<AddCaptioningModal v-model="createOpen" :taken-stream-keys="takenStreamKeys" :limits="limits" @created="onCreated" />
		<ConfirmModal
			modal-id="modal-captioning-list-delete"
			:title="`Delete captioning for ${deleteTarget?.streamName || 'this stream'}?`"
			:message="deleteTarget?.streamDeleted
				? 'The linked livestream is already gone. This removes the captioning flow.'
				: 'Captions stop for viewers. To use a different stream, create a new flow.'"
			ok-text="Delete"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="confirmDelete"
		/>
	</div>
</template>
